import type { MessageKey, Translator } from "~/i18n";

import { browserTimezone, buildCron, nextLocalRuns, type Cadence } from "./scheduleUi";

function fmtTime(h: number, m: number): string {
  const am = h < 12;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${am ? "AM" : "PM"}`;
}

export function dowLabels(t: Translator): readonly string[] {
  return [0, 1, 2, 3, 4, 5, 6].map((i) => t(`workflows.schedule.dow.${i}` as MessageKey));
}

/** Human-readable cron summary in the active UI language (common cadences only). */
export function cronToHumanLocalized(cron: string, t: Translator): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron.trim() || t("workflows.schedule.noSchedule");
  const [mn, hr, dom, mon, dow] = parts as [string, string, string, string, string];
  const m = Number(mn);
  const h = Number(hr);
  const timely = Number.isInteger(m) && Number.isInteger(h);
  const days = dowLabels(t);
  if (dom === "*" && mon === "*" && dow === "*") {
    // every N minutes: "*/N * * * *"
    if (hr === "*" && /^\*\/\d+$/.test(mn)) {
      const n = Number(mn.slice(2));
      return n === 1
        ? t("workflows.schedule.everyMinute")
        : t("workflows.schedule.everyNMinutes", { n: String(n) });
    }
    // every N hours at :mm: "M */N * * *"
    if (/^\*\/\d+$/.test(hr) && Number.isInteger(m)) {
      const n = Number(hr.slice(2));
      return t("workflows.schedule.everyNHours", {
        n: String(n),
        minute: String(m).padStart(2, "0"),
      });
    }
  }
  if (dom === "*" && mon === "*") {
    if (hr === "*" && Number.isInteger(m)) {
      return t("workflows.schedule.everyHour", { minute: String(m).padStart(2, "0") });
    }
    if (dow === "*" && timely) {
      return t("workflows.schedule.everyDayAt", { time: fmtTime(h, m) });
    }
    if (dow === "1-5" && timely) {
      return t("workflows.schedule.everyWeekdayAt", { time: fmtTime(h, m) });
    }
    if (timely && /^[0-6]$/.test(dow)) {
      return t("workflows.schedule.everyDowAt", {
        day: days[Number(dow)] ?? dow,
        time: fmtTime(h, m),
      });
    }
  }
  return cron.trim();
}

export { buildCron, browserTimezone, nextLocalRuns, type Cadence };

/**
 * A run's `detail` as a reader should see it.
 *
 * A scheduled run stores its cron as the detail, so rows read "Schedule · 0 8 * * *" — the same
 * opaque string on every run of that routine. Render a cron-shaped detail in plain English and
 * leave anything else (a sender, a toolkit event) alone.
 */
export function runDetailLabel(detail: string | null | undefined, t: Translator): string {
  const raw = (detail ?? "").trim();
  if (!raw) return "";
  // Five whitespace-separated fields of cron-ish characters.
  if (/^[\d*,\-/]+(?:\s+[\d*,\-/]+){4}$/.test(raw)) return cronToHumanLocalized(raw, t);
  return raw;
}
